import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { fetchSetup } from '@devexpress/analytics-core/analytics-utils-native';
import { environment } from '@root/environments/environment';
import { WebAuth } from 'auth0-js';
import { from, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AUTH0_CONFIG } from '../../../app.constants';
import { ErrorMessage, LoginUserData } from '../core.types';
import { CoreUtilsService } from '../utils/core-utils.service';
import { LocalStorageService } from './local-storage.service';
import { Logger } from './logger.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private localStorageService = inject(LocalStorageService);
  private coreUtils = inject(CoreUtilsService);
  private httpClient = inject(HttpClient);

  private readonly accessTokenSubject = new ReplaySubject<string>(1);
  private readonly log = new Logger('AuthenticationService');
  private jwtHelper = new JwtHelperService();
  private apiUrl: string;

  private readonly refreshTokenSubject = new Subject<string>();
  private isRefreshing = false;

  constructor() {
    this.apiUrl = environment.apiUrl;

    // DevExpress Authentication
    this.getAccessToken$().subscribe((token) => {
      fetchSetup.fetchSettings = {
        headers: { Authorization: `Bearer ${token}` },
      };
    });
  }

  async initialize() {
    this.log.debug('init');
    const storedCredentials =
      this.localStorageService.getCredentialsFromLocalStorage();
    if (
      storedCredentials &&
      storedCredentials.accessToken &&
      !this.jwtHelper.isTokenExpired(storedCredentials.accessToken)
    ) {
      return this.accessTokenSubject.next(storedCredentials.accessToken);
    }
    this.accessTokenSubject.next(await this.getRefreshedAccessToken());
  }

  getAccessToken$(): Observable<string> {
    return this.accessTokenSubject.pipe(
      switchMap((token) => {
        try {
          if (token && !this.jwtHelper.isTokenExpired(token)) {
            return of(token);
          }
        } catch (err) {}
        // If another API call has already started a refresh, wait for it to
        // complete instead of sending the request without a token.
        if (this.isRefreshing) {
          return this.refreshTokenSubject;
        }
        return from(this.getRefreshedAccessToken());
      })
    );
  }

  login(loginUserData: LoginUserData): Promise<ErrorMessage> {
    return new Promise<ErrorMessage>((resolve, reject) => {
      const auth0 = this.getAuth0Client();
      auth0.client.login(
        {
          username: loginUserData.username,
          password: loginUserData.password,
          realm: AUTH0_CONFIG.usernamePasswordRealm,
          scope: AUTH0_CONFIG.scope,
        },
        (err, authResult) => {
          if (!err && authResult && authResult.accessToken) {
            this.localStorageService.saveCredentialsToLocalStorage(authResult);
            this.accessTokenSubject.next(authResult.accessToken);
            resolve(null);
          } else {
            reject(err);
            return;
          }
        }
      );
    });
  }

  logOut() {
    this.localStorageService.clearLocalStorage();
    this.accessTokenSubject.next(null);
  }

  getRefreshedAccessToken(): Promise<string> {
    this.isRefreshing = true;
    return new Promise<string>((resolve) => {
      // Runs on every outcome so a failed refresh can be retried and
      // waiting API calls are released.
      const done = (token: string) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(token);
        resolve(token);
      };
      const refreshToken = this.localStorageService.getRefreshToken();

      if (refreshToken) {
        const options = {
          grantType: AUTH0_CONFIG.refreshTokenGrantType,
          clientID: environment.auth0.clientId,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          refresh_token: refreshToken,
        };
        const auth0 = this.getAuth0Client();
        auth0.client.oauthToken(options, (error, result) => {
          if (error) {
            this.logOut();
            done(null);
            return;
          } else {
            this.localStorageService.saveAccessTokenToLocalStorage(
              result.accessToken
            );
            this.accessTokenSubject.next(result.accessToken);
            done(result.accessToken);
          }
        });
      } else {
        done(null);
      }
    });
  }

  forgotPassword(username: string): Observable<any> {
    this.log.debug('request password reset');
    const payload = this.coreUtils.getRequestPasswordResetPayload(
      environment.auth0.domain,
      username
    );
    return this.httpClient.post(
      `${this.apiUrl}/userAccounts/me/requestPasswordReset`,
      payload
    );
  }

  getUserId() {
    const { userId } =
      this.localStorageService.getCredentialsFromLocalStorage();
    return userId;
  }

  private getAuth0Client() {
    return new WebAuth({
      domain: environment.auth0.domain,
      clientID: environment.auth0.clientId,
      audience: environment.auth0.audience,
      responseType: AUTH0_CONFIG.responseType,
    });
  }
}
